import _ from 'lodash';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, FormikErrors, FormikValues } from 'formik';

import Navbar from '../../../common/Navbar/Navbar';
import Layout from '../../../common/layouts/Layout';
import CreateECMemberForm from './CreateECMemberForm';

import { logger } from '../../../../utils/logger';
import { useCreateEcmUserMutation, useUpdateEcmMutation } from '../../../../generated/graphql';
import { createECMemberValidationSchema, createMemberInitData, editECMemberValidationSchema } from '../utils';
import useAppStore from '../../../../states/zustand/appStore';
import { AVATAR, CV, DATE_OF_APPOINTMENT, DATE_OF_CERTIFICATION, DATE_OF_RESIGNATION, DATE_OF_TENURE_COMPLETION, EMAIL, GCP_CERTIFICATE, GCP_CERTIFICATE_1, GCP_CERTIFICATE_2, IS_AFFILIATED_TO_MEDANTA, MEDICAL_REGISTRATION_CERTIFICATE, MEMBER_TYPE, NAME, NEXT_GCP_CERTIFICATE_DUE_ON, PASSWORD, QUALIFICATION, QUALIFICATION_REGISTRATION, REASON_FOR_RESIGNATION, RESIGN_LETTER, ROLE, ROLES_IN_EC, SIGNED_CONFIDENTIALITY_AGREEMENT, SIGNED_CONFLICT_OF_INTEREST, TRAININGS, VALID_UPTO } from '../../../../constants/forms';

const CreateECMemberContainer = () => {
    const [addEcmMember, {loading}] = useCreateEcmUserMutation();
    const [updateECM, {loading:updateLoader}] = useUpdateEcmMutation();
    const {setLoader} = useAppStore();
    const {id} = useParams();

  useEffect(()=>{
    setLoader(loading || updateLoader)
  },[loading, updateLoader])
    const navigate = useNavigate();
    
    const [steps, setSteps] = useState<number>(0);
    const stepsInfo = useMemo(()=>([
        '1. Member Details',
        '2. Terms of Appointment',
        '3. Qualifications and Registration',
        '4. Good Clinical Practices',
        '5. Other Trainings',
        '6. Terms of Resignation'
    ]), [])

    const stepHandler = (
        validateForm: (values: FormikValues) => Promise<any>,
        values: FormikValues,
        stepIndex: number,
        setErrors?: (errors: FormikErrors<FormikValues>) => void
    ) => {
        validateForm(values).then((res) => {
            if (Object.entries(res).length === 0) {
                // validation success
                const lastStepIndex = stepsInfo?.length - 1;
                if (stepIndex <= -1) {
                    setSteps(0);
                    return;
                } else if (steps === lastStepIndex && stepIndex > lastStepIndex) {
                    saveHandler(values);
                    return;
                } else if (stepIndex <= lastStepIndex) {
                    setSteps((prev) => stepIndex);
                    return;
                }
            } else {
                // validation failed
                logger.error(res);
                if (setErrors) {
                    setErrors(res);
                }
            }
        })
    };

    const saveHandler = async (values: FormikValues) => {
        try {
            const body = {
                [NAME]: values[NAME],
                [ROLE]: values?.[MEMBER_TYPE],
                [GCP_CERTIFICATE]: [values[GCP_CERTIFICATE_1], values[GCP_CERTIFICATE_2]].filter(f => !!f),
                [AVATAR]: values[AVATAR],
                [CV]: values[CV],
                [DATE_OF_TENURE_COMPLETION]: values[DATE_OF_TENURE_COMPLETION],
                [DATE_OF_APPOINTMENT]: values[DATE_OF_APPOINTMENT],
                [DATE_OF_CERTIFICATION]: values[DATE_OF_CERTIFICATION],
                [DATE_OF_RESIGNATION]: values[DATE_OF_RESIGNATION],
                [RESIGN_LETTER]: values[RESIGN_LETTER],
                [IS_AFFILIATED_TO_MEDANTA]: values[IS_AFFILIATED_TO_MEDANTA],
                [MEDICAL_REGISTRATION_CERTIFICATE]: values[MEDICAL_REGISTRATION_CERTIFICATE],
                [MEMBER_TYPE]: values[MEMBER_TYPE],
                [NEXT_GCP_CERTIFICATE_DUE_ON]: values[NEXT_GCP_CERTIFICATE_DUE_ON],
                [QUALIFICATION]: values[QUALIFICATION],
                [REASON_FOR_RESIGNATION]: values[REASON_FOR_RESIGNATION],
                [ROLES_IN_EC]: values[ROLES_IN_EC],
                [SIGNED_CONFIDENTIALITY_AGREEMENT]: values[SIGNED_CONFIDENTIALITY_AGREEMENT],
                [SIGNED_CONFLICT_OF_INTEREST]: values[SIGNED_CONFLICT_OF_INTEREST],
                [TRAININGS]: values[TRAININGS],
                [VALID_UPTO]: values[VALID_UPTO],
            };

            !id ? await addEcmMember({
                variables: {
                    ...body,
                    [EMAIL]: values[EMAIL],
                    [PASSWORD]: values?.[PASSWORD],
                }                
            }) : await updateECM({
                variables: {
                    ...body,
                    _id: id
                }                
            })
            navigate("/");

        } catch (err: any) {
            logger.error(err);
        }

    }

    

    return (
        <Formik
            initialValues={_.cloneDeep(createMemberInitData)}
            validateOnBlur={true}
            enableReinitialize={true}
            validationSchema={!id ? createECMemberValidationSchema[steps] : editECMemberValidationSchema[steps]}
            onSubmit={(values, { setSubmitting, setErrors, validateForm }) => {
                stepHandler(
                    validateForm,
                    values,
                    steps > stepsInfo?.length - 1 ? steps : steps + 1,
                    setErrors
                );
            }}
        >
            <Layout>
                <>
                    <Navbar pageTitle="New Member" />
                    <CreateECMemberForm steps={steps} setSteps={setSteps} stepsInfo={stepsInfo} />
                </>
            </Layout>
        </Formik>
    )
}

export default CreateECMemberContainer;